// src/redux/sagas/menuItemDetailsSaga.ts
import { put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMenuItemDetailsRequest,
  fetchMenuItemDetailsSuccess,
  fetchMenuItemDetailsFailure,
} from '../slices/menuItemDetailsSlice';

function* fetchMenuItemDetailsSaga(action: PayloadAction<string>): any {
  try {
    const itemId = action.payload;
    console.log(`[MenuItemDetailsSaga] 🚀 Request triggered for Item ID:`, itemId);

    // 1. Grab all menu items from the Redux store state
    const state = yield select();
    const allItems = state.menuItems?.items || state.menu?.items || [];
    console.log(`[MenuItemDetailsSaga] 📦 Total items available in store:`, allItems.length);

    // 2. Find the specific item by ID (checking both pk_id and id)
    const foundItem = allItems.find(
      (item: any) => (item.pk_id || item.id)?.toString() === itemId?.toString()
    );

    if (!foundItem) {
      console.error(`[MenuItemDetailsSaga] ❌ Error: Menu item with ID ${itemId} not found in local store!`);
      throw new Error(`Menu item with ID ${itemId} not found in local store`);
    }

    console.log(`[MenuItemDetailsSaga] ✅ Found item in store:`, foundItem);

    // 3. Construct the item strictly matching the target API format
    const formattedItem = {
      id: foundItem.pk_id || foundItem.id,
      restaurantId: foundItem.restaurant_id || foundItem.restaurantId || "",
      name: foundItem.name,
      description: foundItem.description || foundItem.product_description || "",
      price: typeof foundItem.price === 'object' && foundItem.price !== null
        ? foundItem.price 
        : { 
            dining: Number(foundItem.indining_price || foundItem.price || 0), 
            online: Number(foundItem.price?.online || foundItem.large_price || foundItem.price || 0) 
          },
      categoryId: foundItem.categoryId || foundItem.category || "",
      subCategoryId: foundItem.subCategoryId || null,
      imageUrl: foundItem.image || foundItem.imageUrl || null,
      posReferenceId: foundItem.posReferenceId || foundItem.sku_id || "",
      foodType: foundItem.food_type || foundItem.foodType || null,
      meta: foundItem.meta || null,
      mealTime: foundItem.mealTime || null,
      modifiers: foundItem.modifiers || foundItem.modifiers_list || [],
      inventoryStatus: foundItem.inventoryStatus ?? foundItem.is_enabled ?? true,
      isItemVerified: foundItem.isItemVerified ?? foundItem.is_item_verified ?? true,
      isDeleted: foundItem.isDeleted ?? false,
      isAlcoholic: foundItem.isAlcoholic ?? false,
      createdAt: foundItem.createdAt || new Date().toISOString(),
      updatedAt: foundItem.updatedAt || new Date().toISOString(),
    };

    console.log(`[MenuItemDetailsSaga] 🛠️ Formatted Item payload:`, formattedItem);

    // 4. Construct the category matching your required API structure
    const formattedCategory = {
      id: foundItem.categoryId || foundItem.category || "",
      name: foundItem.categoryName || foundItem.category || "Signatures",
      image: null,
      restaurantId: foundItem.restaurant_id || foundItem.restaurantId || "",
      isActive: true,
      sortOrder: 0,
      posReferenceId: foundItem.categoryPosReferenceId || "",
      description: null,
      createdAt: foundItem.createdAt || new Date().toISOString(),
      updatedAt: foundItem.updatedAt || new Date().toISOString(),
    };

    // 5. Extract modifiers / modifierGroups matching the expected array format
    const formattedModifiers = foundItem.modifierGroups || foundItem.modifiers_list || [];
    console.log(`[MenuItemDetailsSaga] ⚙️ Formatted Modifiers payload:`, formattedModifiers);

    // 6. Build the final payload matching the target envelope format
    const itemDetailsPayload = {
      items: [formattedItem],
      categories: [formattedCategory],
      modifiers: formattedModifiers,
    };

    console.log(`[MenuItemDetailsSaga] ✨ Dispatching Success Payload:`, itemDetailsPayload);

    // 7. Dispatch success action
    yield put(fetchMenuItemDetailsSuccess(itemDetailsPayload));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch menu item details';
    console.error(`[MenuItemDetailsSaga] 🚨 Failure caught:`, errorMessage);
    yield put(fetchMenuItemDetailsFailure(errorMessage));
  }
}

export function* watchMenuItemDetailsSaga() {
  yield takeEvery(fetchMenuItemDetailsRequest.type, fetchMenuItemDetailsSaga);
}